import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post, api } from '../lib/api.js';

// ─── Types ────────────────────────────────────────────────────

interface BriefingMetrics {
  new_dark_web_findings: number;
  critical_dark_web_findings: number;
  passwords_exposed: number;
  dmarc_pass_rate: number;
  email_alerts_open: number;
  blacklist_active: boolean;
  phishing_click_rate_this_week: number;
  training_completion_rate: number;
  overdue_training_count: number;
  critical_posture_events: number;
  shadow_ai_violations: number;
  overall_risk_level: 'critical' | 'high' | 'medium' | 'low';
}

interface RecommendedAction {
  priority: 'critical' | 'high' | 'medium';
  action: string;
  rationale: string;
}

interface SecurityBriefing {
  id: string;
  period_start: string;
  period_end: string;
  executive_summary: string;
  dark_web_section: string;
  email_security_section: string;
  human_risk_section: string;
  saas_cloud_section: string;
  recommended_actions: RecommendedAction[];
  metrics_snapshot: BriefingMetrics;
  status: 'draft' | 'approved' | 'sent';
  approved_by?: string;
  approved_at?: string;
  sent_at?: string;
  recipients?: string[];
  custom_intro?: string;
  custom_outro?: string;
  generated_by: 'ai' | 'manual';
  ai_model?: string;
  generation_error?: string;
  created_at: string;
  updated_at: string;
}

interface BriefingConfig {
  id: string;
  enabled: boolean;
  recipients: string[];
  delivery_day: number;
  delivery_hour_utc: number;
  include_sections: string[];
  auto_approve: boolean;
  created_at: string;
  updated_at: string;
}

interface ListResponse {
  data: SecurityBriefing[];
}

interface SingleResponse {
  data: SecurityBriefing;
}

interface ConfigResponse {
  data: BriefingConfig | null;
}

// ─── Helpers ──────────────────────────────────────────────────

function riskBadge(level: BriefingMetrics['overall_risk_level']) {
  const styles: Record<string, string> = {
    critical: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-emerald-100 text-emerald-700',
  };
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[level] ?? 'bg-gray-100 text-gray-600'}`}>
      {level.toUpperCase()}
    </span>
  );
}

function statusBadge(status: SecurityBriefing['status']) {
  const styles: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-600',
    approved: 'bg-blue-100 text-blue-700',
    sent: 'bg-emerald-100 text-emerald-700',
  };
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function actionBg(priority: RecommendedAction['priority']) {
  if (priority === 'critical') return 'bg-red-50 border-l-4 border-red-500';
  if (priority === 'high') return 'bg-orange-50 border-l-4 border-orange-500';
  return 'bg-yellow-50 border-l-4 border-yellow-400';
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Config Card ─────────────────────────────────────────────

interface ConfigCardProps {
  config: BriefingConfig | null;
  onSaved: () => void;
}

function ConfigCard({ config, onSaved }: ConfigCardProps) {
  const [enabled, setEnabled] = useState(config?.enabled ?? true);
  const [autoApprove, setAutoApprove] = useState(config?.auto_approve ?? false);
  const [recipientInput, setRecipientInput] = useState((config?.recipients ?? []).join(', '));
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const recipients = recipientInput
        .split(',')
        .map(r => r.trim())
        .filter(r => r.length > 0);
      await api('/protect/security-briefing?action=config', {
        method: 'PATCH',
        body: JSON.stringify({ enabled, auto_approve: autoApprove, recipients }),
      });
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
      <h3 className="text-base font-semibold text-gray-800 mb-4">Delivery Configuration</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Recipients <span className="text-gray-400 font-normal">(comma-separated emails)</span>
          </label>
          <input
            type="text"
            value={recipientInput}
            onChange={e => setRecipientInput(e.target.value)}
            placeholder="cto@client.com, security@client.com"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
              className="w-4 h-4 text-emerald-600 rounded"
            />
            <span className="text-sm text-gray-700">Enable weekly briefings (Monday 8am UTC)</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoApprove}
              onChange={e => setAutoApprove(e.target.checked)}
              className="w-4 h-4 text-emerald-600 rounded"
            />
            <span className="text-sm text-gray-700">Auto-approve and send without review</span>
          </label>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Config'}
        </button>
      </div>
    </div>
  );
}

// ─── Briefing Detail Panel ────────────────────────────────────

interface DetailPanelProps {
  briefing: SecurityBriefing;
  onBack: () => void;
  onRefresh: () => void;
}

function DetailPanel({ briefing, onBack, onRefresh }: DetailPanelProps) {
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [customIntro, setCustomIntro] = useState(briefing.custom_intro ?? '');
  const [customOutro, setCustomOutro] = useState(briefing.custom_outro ?? '');
  const [savingCustom, setSavingCustom] = useState(false);

  const riskColor =
    briefing.metrics_snapshot.overall_risk_level === 'critical' ? 'text-red-700' :
    briefing.metrics_snapshot.overall_risk_level === 'high' ? 'text-orange-600' :
    briefing.metrics_snapshot.overall_risk_level === 'medium' ? 'text-yellow-600' :
    'text-emerald-600';

  async function handleApprove() {
    setActionLoading('approve');
    try {
      await post(`/protect/security-briefing/${briefing.id}?action=approve`);
      onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleSend() {
    setActionLoading('send');
    try {
      await post(`/protect/security-briefing/${briefing.id}?action=send`);
      onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleSaveCustom() {
    setSavingCustom(true);
    try {
      await post(`/protect/security-briefing/${briefing.id}?action=customize`, {
        custom_intro: customIntro || undefined,
        custom_outro: customOutro || undefined,
      });
      onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save customizations');
    } finally {
      setSavingCustom(false);
    }
  }

  function handlePreviewEmail() {
    const url = `/api/protect/security-briefing/${briefing.id}?action=preview_html`;
    window.open(url, '_blank');
  }

  return (
    <div>
      {/* Back + Actions bar */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className="text-sm text-emerald-600 hover:text-emerald-800 font-medium"
        >
          ← Back to list
        </button>
        <div className="flex items-center gap-2">
          {briefing.status === 'draft' && (
            <button
              onClick={handleApprove}
              disabled={actionLoading === 'approve'}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
            </button>
          )}
          {briefing.status === 'approved' && (
            <button
              onClick={handleSend}
              disabled={actionLoading === 'send'}
              className="px-3 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
            >
              {actionLoading === 'send' ? 'Sending...' : 'Send to Recipients'}
            </button>
          )}
          <button
            onClick={handlePreviewEmail}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
          >
            Preview Email
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <div className="flex items-center gap-3 mb-2">
          <h3 className="text-lg font-bold text-gray-900">
            Week of {formatDate(briefing.period_start)} – {formatDate(briefing.period_end)}
          </h3>
          {statusBadge(briefing.status)}
          {riskBadge(briefing.metrics_snapshot.overall_risk_level)}
        </div>
        {briefing.generation_error && (
          <p className="text-xs text-orange-600 mt-1">Note: {briefing.generation_error} (fallback content used)</p>
        )}
      </div>

      {/* Executive Summary */}
      <div className={`bg-white rounded-lg border-2 border-gray-100 p-5 mb-4`}>
        <h4 className={`text-base font-bold mb-2 ${riskColor}`}>Executive Summary</h4>
        <p className="text-gray-800 text-sm leading-relaxed">{briefing.executive_summary}</p>
      </div>

      {/* 4-column sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-emerald-700 mb-2">Dark Web Status</h4>
          <p className="text-sm text-gray-700 leading-relaxed">{briefing.dark_web_section}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-emerald-700 mb-2">Email Security Update</h4>
          <p className="text-sm text-gray-700 leading-relaxed">{briefing.email_security_section}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-emerald-700 mb-2">Human Risk Update</h4>
          <p className="text-sm text-gray-700 leading-relaxed">{briefing.human_risk_section}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-emerald-700 mb-2">SaaS &amp; Cloud Security</h4>
          <p className="text-sm text-gray-700 leading-relaxed">{briefing.saas_cloud_section}</p>
        </div>
      </div>

      {/* Recommended Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Recommended Actions</h4>
        <div className="space-y-2">
          {briefing.recommended_actions.map((a, i) => (
            <div key={i} className={`p-3 rounded-md ${actionBg(a.priority)}`}>
              <div className="flex items-start gap-2">
                <span className={`text-xs font-bold uppercase mt-0.5 ${
                  a.priority === 'critical' ? 'text-red-700' :
                  a.priority === 'high' ? 'text-orange-600' : 'text-yellow-600'
                }`}>{a.priority}</span>
                <div>
                  <p className="text-sm font-medium text-gray-900">{a.action}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{a.rationale}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Metrics Snapshot */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Metrics Snapshot</h4>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {[
            { label: 'New DW Findings', value: briefing.metrics_snapshot.new_dark_web_findings },
            { label: 'Passwords Exposed', value: briefing.metrics_snapshot.passwords_exposed },
            { label: 'DMARC Pass Rate', value: `${briefing.metrics_snapshot.dmarc_pass_rate}%` },
            { label: 'Phishing Click Rate', value: `${briefing.metrics_snapshot.phishing_click_rate_this_week}%` },
            { label: 'Training Completion', value: `${briefing.metrics_snapshot.training_completion_rate}%` },
            { label: 'Posture Events', value: briefing.metrics_snapshot.critical_posture_events },
          ].map(m => (
            <div key={m.label} className="bg-gray-50 rounded-md p-3 text-center">
              <p className="text-lg font-bold text-gray-900">{m.value}</p>
              <p className="text-xs text-gray-500 mt-1">{m.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Intro/Outro */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Customize Briefing</h4>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Custom Intro (appears before Executive Summary)</label>
            <textarea
              value={customIntro}
              onChange={e => setCustomIntro(e.target.value)}
              rows={2}
              placeholder="Add a personalized message for this client..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Custom Outro (appears at end of email)</label>
            <textarea
              value={customOutro}
              onChange={e => setCustomOutro(e.target.value)}
              rows={2}
              placeholder="Add closing remarks, contact info, or next steps..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleSaveCustom}
              disabled={savingCustom}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
            >
              {savingCustom ? 'Saving...' : 'Save Customizations'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function SecurityBriefingPage() {
  const { data: listData, loading, error, refresh } = useApi<ListResponse>('/protect/security-briefing');
  const { data: configData, refresh: refreshConfig } = useApi<ConfigResponse>('/protect/security-briefing?action=config');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedBriefing, setSelectedBriefing] = useState<SecurityBriefing | null>(null);
  const [generating, setGenerating] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const briefings = listData?.data ?? [];
  const config = configData?.data ?? null;

  async function handleGenerate() {
    setGenerating(true);
    try {
      const result = await post<SingleResponse>('/protect/security-briefing?action=generate');
      refresh();
      if (result?.data) {
        setSelectedBriefing(result.data);
        setSelectedId(result.data.id);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate briefing');
    } finally {
      setGenerating(false);
    }
  }

  async function handleView(briefingId: string) {
    try {
      const result = await api<SingleResponse>(`/protect/security-briefing/${briefingId}`, { method: 'GET' });
      setSelectedBriefing(result.data);
      setSelectedId(briefingId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load briefing');
    }
  }

  async function handleQuickApprove(briefingId: string) {
    setActionLoading(`approve-${briefingId}`);
    try {
      await post(`/protect/security-briefing/${briefingId}?action=approve`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleQuickSend(briefingId: string) {
    setActionLoading(`send-${briefingId}`);
    try {
      await post(`/protect/security-briefing/${briefingId}?action=send`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      setActionLoading(null);
    }
  }

  function handleBack() {
    setSelectedId(null);
    setSelectedBriefing(null);
    refresh();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading security briefings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">AI Security Briefing</h2>
          <p className="text-gray-500 text-sm mt-1">Weekly AI-generated plain-English security summaries for your clients.</p>
        </div>
        {!selectedId && (
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-md hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          >
            {generating ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                Generating...
              </>
            ) : '+ Generate Now'}
          </button>
        )}
      </div>

      {/* Detail view */}
      {selectedId && selectedBriefing && (
        <DetailPanel
          briefing={selectedBriefing}
          onBack={handleBack}
          onRefresh={() => handleView(selectedBriefing.id)}
        />
      )}

      {/* List view */}
      {!selectedId && (
        <>
          {/* Delivery Config */}
          <ConfigCard
            config={config}
            onSaved={refreshConfig}
          />

          {/* Briefings Timeline */}
          <div>
            <h3 className="text-base font-semibold text-gray-800 mb-3">Recent Briefings</h3>
            {briefings.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <p className="text-gray-500 text-sm">No briefings generated yet.</p>
                <p className="text-gray-400 text-xs mt-1">Briefings are generated automatically every Monday at 8am UTC, or click "Generate Now" above.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {briefings.map(b => (
                  <div key={b.id} className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Week of {formatDate(b.period_start)} – {formatDate(b.period_end)}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Generated {formatDate(b.created_at)}
                          {b.sent_at && ` · Sent ${formatDate(b.sent_at)}`}
                          {b.recipients && b.recipients.length > 0 && ` to ${b.recipients.length} recipient${b.recipients.length > 1 ? 's' : ''}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {statusBadge(b.status)}
                        {riskBadge(b.metrics_snapshot.overall_risk_level)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleView(b.id)}
                        className="px-3 py-1.5 text-xs text-emerald-600 border border-emerald-200 rounded-md hover:bg-emerald-50"
                      >
                        View
                      </button>
                      {b.status === 'draft' && (
                        <button
                          onClick={() => handleQuickApprove(b.id)}
                          disabled={actionLoading === `approve-${b.id}`}
                          className="px-3 py-1.5 text-xs text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 disabled:opacity-50"
                        >
                          {actionLoading === `approve-${b.id}` ? 'Approving...' : 'Approve'}
                        </button>
                      )}
                      {b.status === 'approved' && (
                        <button
                          onClick={() => handleQuickSend(b.id)}
                          disabled={actionLoading === `send-${b.id}`}
                          className="px-3 py-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 disabled:opacity-50"
                        >
                          {actionLoading === `send-${b.id}` ? 'Sending...' : 'Send'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
